<template>
    <div class="queue-list-wrapper">
        <div class="queue-labels">
            <div class="queue-label-name">
                <span class="label-text">Name</span>
                <span class="label-count">({{ searchResultTotal }} Tasks)</span>
            </div>
            <div class="queue-label-priority">
                <span class="label-text">Priority</span>
            </div>
        </div>

        <div class="queue-body">
            <div 
                class="queue-scroll" 
                v-if="searchResults.length && !isSpinner" 
                @scroll="scrollFunction"
            >
                <draggable
                    v-model="searchResults"
                    item-key="_id"
                    handle=".queue-drag-handle"
                    ghost-class="sortable-ghost"
                    chosen-class="sortable-chosen"
                    drag-class="sortable-drag"
                    animation="200"
                    :force-fallback="true"
                    fallback-class="sortable-fallback"
                    @start="onDragStart"
                    @end="onDragEnd"
                    @change="onDragChange"
                    class="queue-list"
                >
                    <template #item="{ element, index }">
                        <SingleQueueListComponent
                            :key="element._id"
                            :Taskdata="element"
                            :isDragging="draggingIndex === index"
                            @removeTaskFromQueueData="(ele) => { taskOperations(ele, 'remove') }"
                            @openTaskDetailSidebar="openInNewTab(element)"
                        />
                    </template>
                </draggable>
            </div>
            
            <div class="queue-empty" v-else-if="!searchResults.length && !isSpinner">
                {{ $t('Home.noquelist') }}
            </div>
            
            <div class="queue-loading" v-else>
                <SkelatonVue 
                    v-for="index in 6" 
                    :key="index" 
                    :style="{ height: '48px', marginBottom: '0', borderRadius: '0' }"
                />
            </div>
        </div>

        <div v-if="allProjectsArrayFilter.length">
            <ConvertToSubTaskSidebar 
                @dataToMainComp="(ele) => { taskOperations(ele, 'add') }" 
                :allProjectsArrayFilter="allProjectsArrayFilter" 
                v-if="openConvertSubTaskSidebar === true" 
                :closeSideBar="openConvertSubTaskSidebar" 
                @isConvertSubtaskOPen="(val) => { openConvertSubTaskSidebar = val }" 
                :openMoveSubTask="false" 
                :isMergeTask="false" 
                :isDuplicate="false" 
                :isOpenSubTask="false" 
                :selectedProjectObject="allProjectsArrayFilter[0]" 
                :fromWhich="'dashboard'"
            />
        </div>
        
        <TaskDetail 
            v-if="isTaskDetail" 
            :companyId="companyId" 
            :projectId="projectId" 
            :sprintId="sprintId"
            :taskId="taskId" 
            :isTaskDetailSideBar="isTaskDetail" 
            @toggleTaskDetail="toggleTaskDetail" 
            :zIndex="5" 
        />
    </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, provide, nextTick, watch, defineExpose } from 'vue';
import draggable from 'vuedraggable';
import SkelatonVue from '@/components/atom/Skelaton/Skelaton.vue';
import ConvertToSubTaskSidebar from '@/components/molecules/ConvertToSubTaskSidebar/ConvertToSubTaskSidebar.vue';
import SingleQueueListComponent from './SingleQueueListComponent.vue';
import { useStore } from 'vuex';
import { useToast } from "vue-toast-notification";
import taskClass from "@/utils/TaskOperations";
import { useGetterFunctions } from '@/composable';
import { useI18n } from "vue-i18n";
import { apiRequest } from '@/services';
import * as env from '@/config/env';
import TaskDetail from '@/views/TaskDetail/TaskDetail.vue';

const { t } = useI18n();
const $toast = useToast();
const { getters } = useStore();

const props = defineProps({
    title: {
        type: String,
        required: false,
        default: 'Queue List'
    },
    cardData: {
        type: Object,
        required: true
    },
    cardUID: {
        type: String,
        required: true
    }
});

const emit = defineEmits(['updateQueueOrder']);

watch(() => props.cardData?.statusArray, (newStatusArray) => {
    if (newStatusArray && newStatusArray.length > 0) {
        getAllQueueListData(true);
    }
});

// Computed
const projectsGetter = computed(() => getters["projectData/onlyActiveProjects"]);

// Define Vars
const allProjectsArrayFilter = ref([]);
const searchResults = ref([]);
const searchResultTotal = ref(0);
const isSpinner = ref(false);
const pageNumber = ref(1);
const addcount = ref(0);
const remcount = ref(0);
const timer = ref(null);
const companyId = inject("$companyId");
const userId = inject('$userId');
const openConvertSubTaskSidebar = ref(false);
const { getUser } = useGetterFunctions();

// Draggable state
const draggingIndex = ref(null);
const isDragging = ref(false);

const openAddToQueue = () => {
    if (projectsGetter.value && projectsGetter.value.data?.length) {
        openConvertSubTaskSidebar.value = true;
        allProjectsArrayFilter.value = projectsGetter.value.data.filter((x) => x.id !== '6571e7195470e64b1203295c');
    }
};

defineExpose({ openAddToQueue });

const onDragStart = (evt) => {
    isDragging.value = true;
    draggingIndex.value = evt.oldIndex;
    document.body.style.cursor = 'grabbing';
};

const onDragEnd = () => {
    isDragging.value = false;
    draggingIndex.value = null;
    document.body.style.cursor = '';
};

const onDragChange = (evt) => {
    if (evt.moved) {
        const { element, oldIndex, newIndex } = evt.moved;
        
        emit('updateQueueOrder', {
            taskId: element._id,
            oldIndex,
            newIndex,
            newOrder: searchResults.value.map((item, index) => ({
                _id: item._id,
                order: index
            }))
        });

        saveQueueOrder();
    }
};

const saveQueueOrder = async () => {
    try {
        const orderData = searchResults.value.map((item, index) => ({
            taskId: item._id,
            order: index
        }));
        console.log('Queue order updated:', orderData);
    } catch (error) {
        console.error('Failed to save queue order:', error);
    }
};

const taskOperations = (obj, action) => {
    if (action === 'add') {
        if (props.cardData?.statusArray?.includes(obj.statusKey)) {
            searchResults.value.push(obj);
            searchResultTotal.value += 1;
        }
        openConvertSubTaskSidebar.value = false;
        addcount.value += 1;
    } else if (action === 'remove') {
        let findIndex = searchResults.value.findIndex((ele) => ele._id == obj._id);
        if (findIndex !== -1) {
            searchResults.value.splice(findIndex, 1);
            searchResultTotal.value -= 1;
            remcount.value += 1;
        }
    }
    const user = getUser(userId.value);
    const userData = {
        id: user.id,
        Employee_Name: user.Employee_Name,
        companyOwnerId: user.companyOwnerId
    };
    taskClass.updateQueueList({
        CompanyId: obj.CompanyId,
        projectId: obj.ProjectID,
        sprintId: obj.sprintId,
        taskId: obj._id,
        userId: userId.value,
        actionType: action,
        taskName: obj.TaskName,
        userData: userData,
        queueListArray: obj.queueListArray
    })
    .then(() => {
        if (searchResults.value.length == 9 && searchResultTotal.value - searchResults.value.length > 0) {
            if (addcount.value == 10) {
                addcount.value = 0;
                pageNumber.value += 1;
            }
            if (remcount.value == 10) {
                remcount.value = 0;
                pageNumber.value -= 1;
            }
            getAllQueueListData(false);
        }
        $toast.success(t(`Toast.Queue list ${action == 'add' ? 'added' : 'removed'} successfully`), { position: 'top-right' });
    })
    .catch((error) => {
        console.error("ERROR in update QueueList: ", error);
        $toast.error(t('Toast.queue_list_not_updated'), { position: 'top-right' });
    });
};

const projectId = ref(null);
const sprintId = ref(null);
const taskId = ref(null);
const isTaskDetail = ref(false);

const toggleTaskDetail = (task, close = false) => {
    isTaskDetail.value = false;
    if (close == true) return;
    projectId.value = '';
    sprintId.value = '';
    taskId.value = '';
    nextTick(() => openInNewTab(task));
};
provide('toggleTaskDetail', toggleTaskDetail);

const openInNewTab = (task) => {
    projectId.value = task.ProjectID;
    sprintId.value = task.sprintId;
    taskId.value = task._id;
    isTaskDetail.value = true;
};

onMounted(() => getAllQueueListData());

const getAllQueueListData = (reset = true) => {
    try {
        if (reset) {
            isSpinner.value = true;
            searchResults.value = [];
            pageNumber.value = 1;
        }

        const resultsPerPage = 10;
        const searchQuery = {
            $match: {
                $and: [
                    { objId: { CompanyId: companyId.value } },
                    { AssigneeUserId: { $in: [userId.value] } },
                    { queueListArray: { $in: [userId.value] } },
                    { deletedStatusKey: 0 }
                ]
            }
        };

        if (props.cardData?.statusArray?.length > 0) {
            searchQuery.$match.$and.push({ statusKey: { $in: props.cardData?.statusArray } });
        }

        const query = [{
            $facet: {
                "results": [searchQuery, { $skip: (pageNumber.value - 1) * resultsPerPage }, { $limit: resultsPerPage }],
                "count": [searchQuery, { $count: "count" }]
            }
        }];

        apiRequest('post', `${env.TASK}/find`, { findQuery: query })
            .then((response) => {
                if (response.status !== 200) throw new Error('Failed to fetch queue list data');

                const result = response.data[0].results ?? [];
                const count = response.data[0].count[0]?.count ?? 0;

                if (reset) searchResultTotal.value = count;
                if (!result.length) {
                    isSpinner.value = false;
                    return;
                }

                result.forEach(x => {
                    if (searchResults.value.findIndex((ele) => ele._id === x._id) === -1) {
                        searchResults.value.push({ ...x });
                    }
                });
                pageNumber.value++;
                isSpinner.value = false;
            }).catch((error) => {
                isSpinner.value = false;
                console.error("ERROR in: ", error);
            });
    } catch (error) {
        isSpinner.value = false;
        console.error(error);
    }
};

const scrollFunction = (e) => {
    debouncer(50).then(() => {
        if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - 50) {
            getAllQueueListData(false);
        }
    });
};

function debouncer(timeout = 1000) {
    return new Promise((resolve) => {
        if (timer.value) clearTimeout(timer.value);
        timer.value = setTimeout(() => resolve(), timeout);
    });
}
</script>

<style scoped>
@import '../css/queue-list-style.css';
</style>